/**
 * 首页组件 - SOS紧急求助
 * 极简设计：一键SOS，点击即发送
 */

import { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import NetworkStatus from '../components/NetworkStatus';

type HomeState = 'idle' | 'sending' | 'completed' | 'error';

export default function HomePage() {
  const { state, dispatch, saveSOSRecord, syncRecord } = useApp();
  const { currentLocation } = state;

  const [homeState, setHomeState] = useState<HomeState>('idle');
  const [reportNumber, setReportNumber] = useState<string>('');
  const [showDescInput, setShowDescInput] = useState(false);
  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // 执行SOS
  const executeSOS = async () => {
    setHomeState('sending');

    if (navigator.vibrate) {
      navigator.vibrate([200, 100, 200]);
    }

    try {
      let location = currentLocation;
      if (!location) {
        try {
          const { getCurrentLocation } = await import('../services/location');
          location = await getCurrentLocation(true, 2000);
          dispatch({ type: 'SET_LOCATION', payload: location });
        } catch {
          location = {
            latitude: 0,
            longitude: 0,
            accuracy: -1,
            altitude: null,
            timestamp: Date.now(),
            provider: 'unknown'
          };
        }
      }

      const record = await saveSOSRecord(location, null, '');

      await syncRecord(record);

      setReportNumber(record.reportNumber);
      setHomeState('completed');

      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100, 50, 100]);
      }
    } catch (error) {
      console.error('[首页] SOS失败:', error);
      setErrorMessage('发送失败，数据已保存本地');
      setHomeState('error');
    }
  };

  // 处理描述补充
  const handleDescSubmit = async () => {
    handleClose();
  };

  // 关闭/重置
  const handleClose = () => {
    setHomeState('idle');
    setReportNumber('');
    setDescription('');
    setShowDescInput(false);
    setErrorMessage('');
  };

  // 重试
  const handleRetry = () => {
    setHomeState('idle');
  };

  // 空闲状态 - 全屏SOS按钮
  if (homeState === 'idle') {
    return (
      <div className="min-h-screen bg-bg-dark flex flex-col">
        <header className="p-4 flex justify-between items-center">
          <h1 className="text-lg font-bold text-text-primary">守望者</h1>
          <NetworkStatus />
        </header>

        <main className="flex-1 flex flex-col items-center justify-center p-6">
          <button
            onClick={executeSOS}
            className="w-72 h-72 rounded-full bg-gradient-to-br from-sos-red to-red-700 shadow-2xl shadow-red-500/50 flex items-center justify-center sos-pulse"
          >
            <span className="text-4xl font-bold text-white">SOS</span>
          </button>
          <p className="text-text-muted mt-8 text-center">
            点击按钮立即求助<br/>
            无需注册 · 无需输入
          </p>
        </main>

        <footer className="p-4 text-center">
          <p className="text-text-muted text-xs">
            紧急情况请同时拨打110/120
          </p>
        </footer>
      </div>
    );
  }

  // 发送中状态
  if (homeState === 'sending') {
    return (
      <div className="min-h-screen bg-bg-dark flex flex-col items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-sos-red border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-text-primary text-lg">正在发送求助...</p>
          <p className="text-text-muted text-sm mt-2">请保持手机静止</p>
        </div>
      </div>
    );
  }

  // 完成状态
  if (homeState === 'completed') {
    return (
      <div className="min-h-screen bg-bg-dark flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <div className="text-7xl mb-6">✅</div>
          <h2 className="text-2xl font-bold text-text-primary mb-2">求助已发送</h2>
          <p className="text-text-muted mb-2">编号: {reportNumber}</p>
          <p className="text-text-muted text-sm mb-8">请保持手机畅通</p>

          {/* 可选描述输入 */}
          {!showDescInput ? (
            <div className="space-y-4">
              <button
                onClick={() => setShowDescInput(true)}
                className="block w-full text-text-muted underline text-sm"
              >
                补充情况说明
              </button>
              <button
                onClick={handleClose}
                className="px-8 py-3 bg-sos-red text-white rounded-lg font-medium"
              >
                完成
              </button>
            </div>
          ) : (
            <div className="w-full max-w-xs mx-auto">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="补充现场情况（可选）"
                className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-text-primary placeholder-text-muted/50 mb-3"
                rows={3}
              />
              <div className="flex gap-3">
                <button
                  onClick={handleDescSubmit}
                  className="flex-1 py-2 bg-sos-red text-white rounded-lg"
                >
                  发送
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 py-2 bg-white/10 text-text-muted rounded-lg"
                >
                  跳过
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // 错误状态
  if (homeState === 'error') {
    return (
      <div className="min-h-screen bg-bg-dark flex flex-col items-center justify-center p-6">
        <div className="text-center">
          <div className="text-7xl mb-6">⚠️</div>
          <h2 className="text-2xl font-bold text-text-primary mb-2">发送失败</h2>
          <p className="text-text-muted mb-2">{errorMessage}</p>
          <p className="text-text-muted text-sm mb-8">网络恢复后将自动重试</p>
          <button
            onClick={handleRetry}
            className="px-8 py-3 bg-sos-red text-white rounded-lg font-medium"
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  return null;
}